// Education Content Categories and Tags
// Streamlined to high-yield clinical categories

export const categories = [
  { id: 'cancer', name: 'Cancer', icon: '🎗️' },
  { id: 'voiding', name: 'Voiding Dysfunction', icon: '💧' },
  { id: 'sexual', name: 'Sexual Dysfunction', icon: '♂️' },
  { id: 'stones', name: 'Kidney Stones', icon: '💎' },
  { id: 'procedures', name: 'Procedures', icon: '🏥' }
];

// Content tags mapping - maps content IDs to category IDs
export const contentTags = {
  // ============ CANCER ============
  // Prostate Cancer
  'prostate-cancer': ['cancer'],
  'elevated-psa': ['cancer'],
  'prostate-biopsy': ['cancer', 'procedures'],
  'psa': ['cancer'],
  // Bladder Cancer
  'bladder-cancer': ['cancer'],
  'bcg-therapy': ['cancer', 'procedures'],
  'cystoscopy': ['cancer', 'procedures'],
  'hematuria': ['cancer', 'voiding'],
  // Kidney Cancer
  'kidney-cancer': ['cancer'],
  'renal-cysts': ['cancer'],
  'bosniak-3-cysts': ['cancer'],
  'small-renal-mass': ['cancer'],
  // Testicular Cancer
  'testicular-cancer': ['cancer'],
  // Upper Tract Urothelial Cancer
  'upper-tract-urothelial': ['cancer'],
  // Urethral Cancer
  'urethral-cancer': ['cancer'],
  // Penile Cancer
  'penile-cancer': ['cancer'],
  // Adrenal Cancer
  'adrenal-cancer': ['cancer'],
  
  // ============ VOIDING DYSFUNCTION ============
  // BPH / Prostate
  'bph': ['voiding'],
  'enlarged-prostate': ['voiding'],
  'urinary-retention-catheter': ['voiding', 'procedures'],
  // Overactive Bladder / Incontinence
  'overactive-bladder': ['voiding'],
  'incontinence': ['voiding'],
  'nocturia': ['voiding'],
  'post-void-dribbling': ['voiding'],
  'pelvic-floor-pt': ['voiding', 'procedures'],
  // UTI / Infections
  'uti': ['voiding'],
  'recurrent-uti': ['voiding'],
  'bladder-infection': ['voiding'],
  'urethritis': ['voiding'],
  'prostatitis': ['voiding', 'sexual'],
  // Catheters
  'catheter-removal': ['voiding', 'procedures'],
  'catheter-flushing': ['voiding', 'procedures'],
  'nephrostomy-care': ['voiding', 'procedures'],
  // Cloudy Urine
  'cloudy-urine': ['voiding'],
  
  // ============ SEXUAL DYSFUNCTION ============
  'erectile-dysfunction': ['sexual'],
  'low-testosterone': ['sexual'],
  'painful-ejaculation': ['sexual'],
  'blood-in-semen': ['sexual'],
  'varicocele': ['sexual'],
  'varicoceles': ['sexual'],
  'male-infertility': ['sexual'],
  'vasectomy': ['sexual', 'procedures'],
  'testicular-pain': ['sexual'],
  'epididymitis': ['sexual'],
  'scrotal-cysts': ['sexual'],
  'genital-warts': ['sexual'],
  'hydrocele-spermatocele': ['sexual'],
  
  // ============ KIDNEY STONES ============
  'kidney-stones': ['stones'],
  'kidney-stone-conservative': ['stones'],
  'kidney-stone-ed-followup': ['stones'],
  'ureteroscopy': ['stones', 'procedures'],
  'lithotripsy': ['stones', 'procedures'],
  'ureteral-stent': ['stones', 'procedures'],
  'post-ureteroscopy': ['stones', 'procedures'],
  'hydronephrosis': ['stones'],
  
  // ============ PROCEDURES ============
  'turp': ['procedures', 'voiding'],
  
  // ============ ANATOMY (no category filter - always shown) ============
  'prostate': [],
  'bladder': [],
  'kidney': [],
  'ureter': [],
  'urethra': [],
  'testicle': []
};

// Symptom keywords for search
export const symptomKeywords = {
  'blood in urine': ['hematuria', 'bladder-cancer', 'kidney-stones', 'uti'],
  'painful urination': ['uti', 'prostatitis', 'bladder-infection'],
  'burning urination': ['uti', 'prostatitis', 'urethritis'],
  'frequent urination': ['bph', 'overactive-bladder', 'uti', 'prostatitis', 'nocturia'],
  'difficulty urinating': ['bph', 'urinary-retention', 'prostatitis'],
  'weak stream': ['bph', 'prostatitis'],
  'urgency': ['overactive-bladder', 'uti', 'bph'],
  'incontinence': ['incontinence', 'overactive-bladder', 'post-void-dribbling'],
  'leaking urine': ['incontinence', 'overactive-bladder', 'post-void-dribbling'],
  'back pain': ['kidney-stones', 'kidney-infection'],
  'flank pain': ['kidney-stones', 'kidney-infection', 'hydronephrosis'],
  'groin pain': ['kidney-stones', 'testicular-pain'],
  'testicular pain': ['testicular-pain', 'epididymitis', 'varicocele'],
  'scrotal swelling': ['varicocele', 'hydrocele-spermatocele', 'scrotal-cysts', 'testicular-pain'],
  'erectile problems': ['erectile-dysfunction'],
  'fever': ['kidney-infection', 'prostatitis', 'uti', 'epididymitis'],
  'nighttime urination': ['nocturia', 'bph', 'overactive-bladder'],
  'dribbling': ['post-void-dribbling', 'bph']
};

// Calculate estimated reading time
export const calculateReadingTime = (content) => {
  if (!content) return 1;
  const wordsPerMinute = 200;
  const words = content.split(/\s+/).length;
  const minutes = Math.ceil(words / wordsPerMinute);
  return Math.max(1, minutes);
};

// Get category name by ID
export const getCategoryName = (categoryId) => {
  const category = categories.find(c => c.id === categoryId);
  return category ? category.name : categoryId;
};

// Get tags for a content item
export const getContentTags = (contentId) => {
  return contentTags[contentId] || [];
};

// Check if content belongs to a category
export const contentInCategory = (contentId, categoryId) => {
  const tags = contentTags[contentId] || [];
  return tags.includes(categoryId);
};
